import { type FC, useCallback, useRef, useState } from 'react';

import type { Editor } from '@tiptap/react';
import cn from 'classnames';

import type { CommunityPostComposerProps } from '../../CommunityPostComposer.type';
import { DEFAULT_UPLOAD_ACCEPT, DEFAULT_UPLOAD_MAX_BYTES, tr } from '../constants';
import { landOnTextblock } from '../landOnTextblock';

interface MediaUploadTabProps {
  editor: Editor;
  translations?: Record<string, string>;
  onUploadImage: NonNullable<CommunityPostComposerProps['onUploadImage']>;
  isMediaUploadDisabled?: CommunityPostComposerProps['isMediaUploadDisabled'];
  onInserted: () => void;
  uploadAccept?: string;
  uploadMaxBytes?: number;
}

export const MediaUploadTab: FC<MediaUploadTabProps> = ({
  editor,
  translations,
  onUploadImage,
  isMediaUploadDisabled = false,
  onInserted,
  uploadAccept = DEFAULT_UPLOAD_ACCEPT,
  uploadMaxBytes = DEFAULT_UPLOAD_MAX_BYTES,
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (isMediaUploadDisabled) return;

      const oversizedFiles = files.filter((file) => file.size > uploadMaxBytes);
      const validFiles = files.filter((file) => file.size <= uploadMaxBytes);

      if (validFiles.length === 0) {
        setErrors(
          oversizedFiles.map((file) => `${file.name}: ${tr(translations, 'community.composer.upload_file_too_large')}`),
        );
        return;
      }

      setLoading(true);
      setErrors([]);

      try {
        const results = await Promise.allSettled(validFiles.map((file) => onUploadImage(file)));

        const imageNodes = results.flatMap((res, idx) => {
          if (res.status !== 'fulfilled' || !res.value) return [];
          const file = validFiles[idx];
          return [
            {
              type: 'image',
              attrs: { src: res.value.url, alt: res.value.alt ?? file.name, blobKey: res.value.blobKey },
            },
          ];
        });

        if (imageNodes.length > 0) {
          const pos = editor.state.selection.to;
          editor.chain().focus().insertContentAt(pos, imageNodes).command(landOnTextblock).run();
        }

        const nextErrors = [
          ...oversizedFiles.map(
            (file) => `${file.name}: ${tr(translations, 'community.composer.upload_file_too_large')}`,
          ),
          ...results.flatMap((result, idx) =>
            result.status === 'rejected' || !result.value
              ? [`${validFiles[idx].name}: ${tr(translations, 'community.composer.upload_error')}`]
              : [],
          ),
        ];

        setErrors(nextErrors);

        if (imageNodes.length > 0) onInserted();
      } finally {
        setLoading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [editor, isMediaUploadDisabled, onUploadImage, uploadMaxBytes, onInserted, translations],
  );

  return (
    <div className="flex flex-col items-center gap-3 p-6">
      <input
        ref={inputRef}
        type="file"
        accept={uploadAccept}
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          if (files.length > 0) handleUpload(files);
        }}
      />
      <button
        type="button"
        disabled={loading || isMediaUploadDisabled}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'inline-flex h-10 items-center justify-center rounded-full px-5 text-[13px] font-medium leading-4 tracking-[-0.13px] bg-black text-white',
          {
            'opacity-60 cursor-not-allowed': loading || isMediaUploadDisabled,
            'hover:bg-black/90': !loading && !isMediaUploadDisabled,
          },
        )}
      >
        {loading
          ? tr(translations, 'community.composer.upload_loading')
          : tr(translations, 'community.composer.upload_choose_file')}
      </button>
      <p className="text-xs text-black/50 text-center">{tr(translations, 'community.composer.upload_hint')}</p>
      {errors.length > 0 && (
        <ul className="w-full text-sm text-red-500">
          {errors.map((error) => (
            <li key={error} className="text-left break-words">
              {error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
